import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { UserExtendedDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { PencilLineIcon } from "@/components/Icons/PencilLineIcon";
import { UserPlus01Icon } from "@/components/Icons/UserPlus01Icon";
import { User } from "@/components/User/User";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import {
  createImageSizeRule,
  createImageTypeRule,
  createRequiredStringRule,
  createTrimmedMaxLengthStringRule,
  createTrimmedMinLengthStringRule,
} from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { GROUP_NAME_MAX_LENGTH, GROUP_NAME_MIN_LENGTH } from "@/modules/chats/components/CreateChatModal";
import { useChatQueries } from "@/queries/chats/queryOptions";
import { useTranslation } from "@/translations";

import { canEditGroupChat } from "../../helper";
import type { EditGroupChatFormValues } from "./EditGroupChatModal";

interface EditGroupChatModalInfoViewProps {
  canLeave: boolean;
  groupChatId: string;
  isModalOpen: boolean;
  isUpdating: boolean;
  onAddMembers: () => void;
  onLeaveGroup: () => void;
}
export function EditGroupChatModalInfoView({
  groupChatId,
  isModalOpen,
  isUpdating,
  onAddMembers,
  onLeaveGroup,
  canLeave,
}: EditGroupChatModalInfoViewProps): React.ReactNode {
  const [isEditingName, editNameHandlers] = useBool(false);
  const { t } = useTranslation();
  const formMethods = useFormContext<EditGroupChatFormValues>();
  const sessionUser = useSessionUser();
  const chatQueries = useChatQueries();
  const { data: members, isPending: isPendingMembers } = useQuery({
    ...chatQueries.chatMembers({ chatId: groupChatId }),
    enabled: isModalOpen,
  });
  const onConfirmNewName = async (isForced = false) => {
    const isNewGroupNameValid = await formMethods.trigger("groupName");
    const newGroupName = formMethods.getValues("groupName");
    if (isNewGroupNameValid) {
      formMethods.setValue("groupName", newGroupName.trim());
      editNameHandlers.setFalse();
    } else if (!isNewGroupNameValid && isForced) {
      formMethods.setValue("groupName", formMethods.formState.defaultValues!.groupName!, {
        shouldDirty: true,
      });
    }
  };

  // Handle unconfirmed name when view gets unmounted
  useEffect(() => {
    return () => {
      void onConfirmNewName(true);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const currGroupName = useWatch<EditGroupChatFormValues, "groupName">({
    name: "groupName",
  });

  const canEdit = canEditGroupChat({
    isMember: true,
    chatPermission: sessionUser.chatPermission,
  });

  return (
    <Stack gap="lg">
      <Stack gap="sm">
        {/* Header */}
        <span className="self-center">
          <FormField label={t("component.group-chat-info-modal.form.image.label")} hideLabel>
            <FormImageInput<EditGroupChatFormValues, "groupImage">
              name="groupImage"
              rules={{
                validate: {
                  size: createImageSizeRule({
                    t,
                  }),
                  type: createImageTypeRule({
                    t,
                  }),
                },
              }}
              nOfImages={1}
              isAvatarInput
              cropper
              disabled={!canEdit}
            />
          </FormField>
        </span>
        <Inline gap="sm" align="center" justify="center">
          {isEditingName ? (
            <>
              <FormField label={t("component.group-chat-info-modal.form.name.label")} hideLabel required>
                <FormInput<EditGroupChatFormValues, "groupName">
                  data-testid="group-name-input"
                  name="groupName"
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "component.group-chat-info-modal.form.name.label"),
                      minLength: createTrimmedMinLengthStringRule({
                        t,
                        fieldName: "component.group-chat-info-modal.form.name.label",
                        minLength: GROUP_NAME_MIN_LENGTH,
                      }),
                      maxLength: createTrimmedMaxLengthStringRule({
                        t,
                        fieldName: "component.group-chat-info-modal.form.name.label",
                        maxLength: GROUP_NAME_MAX_LENGTH,
                      }),
                    },
                  }}
                  autoFocus
                />
              </FormField>
              <IconButton
                data-testid="confirm-group-name-btn"
                title={t("component.group-chat-info-modal.confirm-name.btn")}
                variant="ghostPrimary"
                size="sm"
                onClick={() => onConfirmNewName()}
                withTooltip={false}
                icon={<CheckIcon />}
              />
            </>
          ) : (
            <>
              <span className="w-fit max-w-measure-compact truncate text-heading-2">{currGroupName}</span>
              {canEdit && (
                <IconButton
                  data-testid="edit-group-name-btn"
                  title={t("component.group-chat-info-modal.edit-name.btn")}
                  variant="ghostPrimary"
                  size="sm"
                  onClick={editNameHandlers.setTrue}
                  withTooltip={false}
                  icon={<PencilLineIcon />}
                />
              )}
            </>
          )}
        </Inline>
      </Stack>
      <EditGroupChatModalMemberListView
        members={members || []}
        isLoading={isPendingMembers}
        onClickAddMembers={onAddMembers}
      />
      <footer className="flex w-full flex-1 justify-between">
        {canLeave && (
          <Button variant="subtle" tone="error" onClick={onLeaveGroup} data-testid="modal-leave-group-btn">
            {t("component.group-chat-info-modal.leave-group.btn")}
          </Button>
        )}
        {canEdit && (
          <span className="ml-auto w-fit">
            <Button
              data-testid="modal-save-changes-btn"
              variant="filled"
              isLoading={isUpdating}
              disabled={!formMethods.formState.isDirty || isEditingName}
              type="submit"
            >
              {t("component.group-chat-info-modal.save-changes.btn")}
            </Button>
          </span>
        )}
      </footer>
    </Stack>
  );
}
interface EditGroupChatModalMemberListViewProps {
  members: UserExtendedDto[];
  isLoading: boolean;
  onClickAddMembers: () => void;
}
function EditGroupChatModalMemberListView({
  members,
  isLoading,
  onClickAddMembers,
}: EditGroupChatModalMemberListViewProps): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();

  const canEdit = canEditGroupChat({
    isMember: true,
    chatPermission: sessionUser.chatPermission,
  });

  return (
    <Stack gap="sm">
      <Spread>
        <span className="text-caption-emphasis">
          {t("component.group-chat-info-modal.members-count", {
            count: members.length || 0,
          })}
        </span>
        {canEdit && (
          <Button variant="ghostPrimary" icon={<UserPlus01Icon />} onClick={onClickAddMembers}>
            {t("component.group-chat-info-modal.action.add-members")}
          </Button>
        )}
      </Spread>
      <Card as="ul" density="flush" borderTone="secondary" scroll="compact">
        {isLoading && (
          <li className="absolute inset-0 flex items-center justify-center">
            <Spinner size="md" />
          </li>
        )}
        {!isLoading &&
          members.length > 0 &&
          members.map((member) => (
            <li
              key={member.id}
              className="flex w-full items-center gap-space-sm overflow-x-hidden border-b border-border-secondary px-space-md py-space-sm last:rounded-b-lg last:border-b-0 even:bg-background-secondary"
            >
              <User user={member} renderSubtitle={() => member.locatedAt} />
            </li>
          ))}
      </Card>
    </Stack>
  );
}
